import { Link } from 'react-router-dom'
import { useFavorites } from '@/hooks/useFavorites'
import type { Movie } from '@/mocks/movies'

interface MovieCardProps {
  movie: Movie
  showCategory?: boolean
}

const categoryLabel: Record<Movie['category'], string> = {
  movie: '电影',
  tv: '电视剧',
  short: '短片',
}

export default function MovieCard({ movie, showCategory = true }: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites()
  const favorited = isFavorite(movie.id)

  return (
    <article className="group flex flex-col">
      <div className="relative">
        <Link
          to={`/detail/${movie.id}`}
          aria-label={`查看《${movie.title}》详情`}
          className="block overflow-hidden rounded-lg bg-background-200"
        >
          <div className="relative aspect-[3/4] w-full">
            <img
              src={movie.poster}
              alt={`《${movie.title}》封面`}
              title={`${movie.title} - 麻豆传媒在线影视作品`}
              className="h-full w-full object-cover object-top transition-transform duration-700 ease-portal group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/5 to-transparent" />

            {showCategory && (
              <span className="absolute left-2 top-2 rounded bg-primary-950/75 px-2 py-0.5 text-[10px] tracking-wide text-background-100">
                {categoryLabel[movie.category]}
              </span>
            )}

            <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded bg-background-50/90 px-1.5 py-0.5 text-[11px] font-medium text-foreground-900">
              <i className="ri-star-fill text-[10px] text-accent-500" />
              {movie.rating.toFixed(1)}
            </span>

            <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-background-50/60 bg-background-50/20 backdrop-blur-sm">
                <i className="ri-play-fill text-xl text-background-50" />
              </span>
            </span>
          </div>
        </Link>

        <button
          type="button"
          onClick={() => toggleFavorite(movie.id)}
          aria-label={favorited ? '取消收藏' : '收藏该作品'}
          aria-pressed={favorited}
          className={`absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
            favorited
              ? 'bg-accent-500 text-background-50'
              : 'bg-primary-950/50 text-background-100 hover:bg-primary-950/75'
          }`}
        >
          <i className={favorited ? 'ri-heart-3-fill text-sm' : 'ri-heart-3-line text-sm'} />
        </button>
      </div>

      <div className="mt-3 flex flex-col">
        <Link
          to={`/detail/${movie.id}`}
          className="line-clamp-1 text-sm font-medium text-foreground-950 transition-colors hover:text-accent-700"
        >
          {movie.title}
        </Link>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-foreground-500">
          <span className="font-label tracking-wide">{movie.year}</span>
          <span className="text-background-400">/</span>
          <span className="line-clamp-1">{movie.genres.join(' · ')}</span>
        </p>
      </div>
    </article>
  )
}